<template>
  <div class="sf-grid sf-grid-fluid sf-grid-gutter-none" :class="outerClass">
    <div v-if="isEmpty" class="sf-col sf-pad-3 sf-alignc">
      <div class="workflow-review_empty-state-container">
        <div class="sf-empty-state">
          <img
            class="sf-empty-state-image"
            :src="$imagesRequire('empty_states/client-file.svg')"
          />
          <div class="sf-empty-state-title">
            {{ I18n.t('workflow_review.empty_state.title') }}
          </div>
          <div class="sf-empty-state-text sf-x-text">
            <div
              v-if="filter !== null"
              v-dompurify-html="
                I18n.t('workflow_review.empty_state.message_filter_html')
              "
              @click.prevent.stop="clearFilter"
            />
            <div v-else>
              {{ I18n.t('workflow_review.empty_state.message') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <ScrollableContent
      v-else
      ref="scrollableContent"
      :current-index="currentIndex"
      @item-focused="onObserverItemFocused"
      @scroll="onContainerScroll"
    >
      <template #default="{ observer, getScrollableRef }">
        <div
          v-if="compareMode && !isEmpty"
          class="
            sf-grid
            sf-grid-fluid
            sf-grid-gutter-medium
            sf-layer-border-bottom-dark
            tw-sticky tw-top-0 tw-bg-[#f1f3f5] tw-z-[5]
          "
        >
          <div
            class="
              sf-row
              sf-row-nowrap
              sf-divide-h-solid
              sf-divide-snow-darker
              sf-below-0
              sf-pad-h-3
            "
            :class="hideSessionClass"
          >
            <div class="sf-col sf-md-6 sf-level sf-pad-v-2">
              <span class="sf-pad-r-1">{{
                I18n.t('workflow_review.current_period')
              }}</span>
              <span class="sf-bold">{{ ledgerLabel }}</span>
            </div>
            <div class="sf-col sf-md-6 sf-level sf-pad-v-2">
              <div v-if="!compareLedgerId">
                {{ I18n.t('workflow_review.nothing_to_compare') }}
              </div>
              <div v-else class="sf-level">
                <span class="sf-pad-r-1">{{
                  I18n.t('workflow_review.previous_period')
                }}</span>
                <div ref="periodPickerDropdown" class="sf-dropdown">
                  <div v-if="reloadingLedger" class="sf-spinner" />
                  <SfButtonDs
                    v-else
                    icon-after="caret-down"
                    data-toggle="dropdown"
                  >
                    {{ compareLedgerLabel }}
                  </SfButtonDs>
                  <PeriodPicker
                    class="sf-dropdown-menu"
                    :current-ledger-id="compareLedgerId"
                    :years="periodPickerYears"
                    :ledgers-per-year="comparePeriodPickerData.ledgers_per_year"
                    @change-compare-period="periodPicked"
                  />
                </div>
              </div>
              <div
                class="dropdown-menu dropdown-menu-periodpicker"
                data-behaviour="periodpicker set-current-ledger"
              />
            </div>
          </div>
        </div>
        <div ref="reviewItemsContent" class="tw-pb-[70vh] tw-z-0 tw-relative">
          <div data-walkthrough-id="workflow-review-list">
            <WorkflowReviewItemList
              :items="items"
              :context-id="contextId"
              :total="itemLength"
              :current-index="currentIndex"
              :next-item-index-to-focus="nextReviewItemIndexToFocus"
              :observer="observer"
              :get-scrollable-ref="getScrollableRef"
              :fetching-communications-status="fetchingCommunicationsStatus"
            />
          </div>
        </div>
      </template>
    </ScrollableContent>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import humps from 'humps'
import debounce from 'lodash/debounce'
import { v4 as generateUuid } from 'uuid'
import { nextTick } from 'vue'

import WorkflowReviewItemList from './workflow-review-item-list'
import WorkflowReviewSidepane from './workflow-review-sidepane'
import ScrollableContent from './scrollable-content'
import PeriodPicker from '@app/application/layout/components/period-picker'
import { SfButtonDs } from '@silverfin/ui-components'
import Polling from '@app/utils/polling'
import { setQueryParam, getQueryParam } from '@app/utils/url-helpers'
import { createBatchesMiddleOut } from '@app/utils/batch-helpers'
import { LazyBatchLoader } from '../services/lazy-batch-loader'
import { FocusedScrollAnchor } from '../services/focused-scroll-anchor'
import { useComparisonModeToggle } from '@app/application/workflows/composables/use-comparison-mode-toggle'
import { usePersistFocusedItem } from '@app/application/workflows/composables/use-persist-focused-item'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import { useContext } from '@app/shared/pinia/context'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useWorkflowReviewFilter } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-filter'
import { useWorkflowReviewFocus } from '../store/pinia/workflow-review-focus'
import { useWorfklowReviewItems } from '../store/pinia/workflow-review-items'
import { useWorkflowReviewItemStatus } from '../store/pinia/workflow-review-item-status'
import { useWorfklowUI } from '../store/pinia/workflow-ui'

import api from '../api'
import { decompressLiquidItem } from '../services/decompress-liquid-item'
import { SfLog } from '@app/adapters/logger'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { isUsingTwoDrawerLayout } from '@app/utils/domHelpers'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import { useSidebarFilePreviewStore } from '@app/application/sidebar-file-preview/store/sidebar-file-preview'
import { RightDrawerContent } from '@app/shared/types/drawer'
import { DocumentLinkType } from '@app/shared/types/sf-data'
import { refreshSolidData } from '@app/adapters/solid/refresh-solid-data'

const WorkflowReview = {
  name: 'WorkflowReview',
  components: {
    WorkflowReviewItemList,
    WorkflowReviewSidepane,
    ScrollableContent,
    PeriodPicker,
    SfButtonDs,
  },
  setup(props) {
    const { turnOnCompareMode } = useComparisonModeToggle(false)
    const hideSessionClass = useRedactSessionRecording()

    usePersistFocusedItem({
      ledgerId: props.ledgerId,
      workflowId: props.workflowId,
    })

    return {
      turnOnCompareMode,
      hideSessionClass,
    }
  },
  props: {
    workflowId: {
      type: [String, Number],
      required: true,
    },
    ledgerId: {
      type: [String, Number],
      required: true,
    },
    ledgerLabel: {
      type: String,
      required: true,
    },
    compareLedgerData: {
      type: Object,
      required: true,
    },
    initialCompareMode: {
      type: Boolean,
      required: true,
    },
    initialItemsRange: {
      type: Object,
      required: true,
    },
    initialReviewItems: {
      type: Array,
      required: false,
      default: () => [],
    },
    batchSize: {
      type: Number,
      required: true,
    },
    messageBusChannelName: {
      type: String,
      required: false,
      default: null,
    },
    messageBusLastId: {
      type: String,
      required: false,
      default: null,
    },
    sidepaneFixed: {
      type: Boolean,
      required: false,
      default: false,
    },
    useAssistantChecks: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      requestRef: generateUuid(),
      compareLedgerLabel: this.compareLedgerData.compare_ledger_label,
      comparePeriodPickerData:
        this.compareLedgerData.compare_period_picker_data,
      lazyBatchLoader: null,
      scrollAnchor: null,
      liquidUpdateChain: Promise.resolve(),
    }
  },
  computed: {
    ...mapState({
      contextId: (state) => state.contextId,
      contextType: (state) => state.contextType,
      fetchingCommunicationsStatus: (state) =>
        state.ui.fetchingCommunicationsStatus,
    }),
    ...mapPiniaState(useDrawer, ['isAnyDrawerOpen', 'isRightDrawerOpen']),
    ...mapPiniaState(useWorkflowReviewFilter, ['filter']),
    ...mapPiniaState(useWorkflowReviewFocus, ['nextReviewItemIndexToFocus']),
    ...mapPiniaState(useWorfklowReviewItems, [
      'currentIndex',
      'currentReviewItem',
      'isEmpty',
      'itemLength',
      'reviewItems',
    ]),
    ...mapPiniaState(useWorfklowUI, [
      'compareLedgerId',
      'compareMode',
      'reloadingLedger',
    ]),
    actionBarClass() {
      return {
        'sf-pad-h-3': !this.isEmpty,
        'workflow-review_action-bar--empty': this.isEmpty,
      }
    },
    batches() {
      const indexes = Array.from(Array(this.items.length).keys())

      return createBatchesMiddleOut(
        indexes,
        this.initialItemsRange.bottom,
        this.initialItemsRange.top,
        this.batchSize
      )
    },
    items() {
      return this.reviewItems.map((item, index) => ({ ...item, index }))
    },
    outerClass() {
      return this.compareMode && this.isAnyDrawerOpen && !isUsingNewLayout()
        ? 'workflow-review--narrow'
        : ''
    },
    periodPickerYears() {
      return this.comparePeriodPickerData.years.map((year) =>
        humps.camelizeKeys(year)
      )
    },
  },
  watch: {
    currentReviewItem(newValue, oldValue) {
      if (
        newValue &&
        (newValue.id !== oldValue?.id ||
          newValue.itemClass !== oldValue?.itemClass)
      ) {
        this.setContextName(newValue.itemName)
      }
    },
    isRightDrawerOpen(isOpen) {
      if (isOpen && this.items[this.currentIndex]) {
        const { contextType, contextId, contextHrefParam } =
          this.items[this.currentIndex]
        this.setCommunicationContext({
          contextType,
          contextId,
          contextHrefParam,
        })
      }
    },
    nextReviewItemIndexToFocus(newValue, oldValue) {
      if (
        newValue !== oldValue &&
        newValue !== null &&
        newValue !== this.currentIndex
      ) {
        this.handleNewItemFocus(newValue)
        this.lazyBatchLoader?.prefetchBatchForItem(newValue)
        this.scrollFocusedIntoView()
      }
    },
  },
  created() {
    this.receiveReviewItems(this.initialReviewItems)
    this.receiveItemStatuses(this.initialReviewItems)
    this.setCompareLedgerId(this.compareLedgerData.compare_ledger_id)

    this.debounceSetCommunicationContext = debounce(
      this.debouncedSetCommunicationContext,
      500
    )

    // TO-DO: Only keep this one when we handle context outside of the comms store
    this.setContext(this.contextType, this.contextId)

    if (!window.Silverfin?.isCurrentUserPulse?.()) {
      this.fetchChangesSinceLastVisit({
        contextId: this.contextId,
        contextType: this.contextType,
      })
    }

    this.updateCompareMode(this.initialCompareMode)

    window.eventHub.$on(
      'refreshCommunicationData',
      this.onRefreshCommunicationData
    )

    // The drawer store announces open/close before either layout flips the
    // drawer's DOM classes (both defer that to requestAnimationFrame), so
    // these handlers run while the list still has its pre-reflow geometry —
    // in time to snapshot and hold it.
    window.eventHub.$on('drawer:open', this.holdListAcrossDrawerReflow)
    window.eventHub.$on('drawer:close', this.holdListAcrossDrawerReflow)

    this.fetchAssistantChecks(this.currentIndex)

    this.subscribeToLiquidUpdates()
  },
  mounted() {
    this.initLazyBatchLoading()

    // One refresh covers the period, whichever batch renders the Solid items.
    const solidItem = this.items.find((item) => item.solidTemplate)

    if (solidItem) {
      refreshSolidData(solidItem.solidTemplate.requestContext)
    }

    const documentId = getQueryParam('document_id')
    const documentName = getQueryParam('document_name')
    const documentSize = getQueryParam('document_size')
    const documentPreviewable = getQueryParam('document_previewable')

    if (documentId && documentName && documentSize) {
      this.setAttachment({
        name: documentName,
        size: documentSize,
        previewable:
          documentPreviewable !== undefined
            ? documentPreviewable !== 'false'
            : true,
        document_id: documentId,
        linkType: DocumentLinkType.Document,
      })
      const drawer = {
        direction: 'right',
        content: RightDrawerContent.FilePreview,
      }
      this.openDrawer(drawer)
    }
  },
  async beforeUnmount() {
    window.eventHub.$off(
      'refreshCommunicationData',
      this.onRefreshCommunicationData
    )
    window.eventHub.$off('drawer:open', this.holdListAcrossDrawerReflow)
    window.eventHub.$off('drawer:close', this.holdListAcrossDrawerReflow)
    if (this.polling) {
      this.polling.unsubscribe()
    }
    if (this.lazyBatchLoader) {
      this.lazyBatchLoader.destroy()
      this.lazyBatchLoader = null
      delete window.__sfLazyBatchLoader
    }
    if (this.scrollAnchor) {
      this.scrollAnchor.destroy()
      this.scrollAnchor = null
    }
    this.$refs.scrollableContent?.$el?.removeEventListener(
      'click',
      this.holdTemplateOpeningDrawer,
      true
    )
    await api.cancelWorkflowReviewJobs({
      ledgerId: this.ledgerId,
      workflowId: this.workflowId,
      requestRef: this.requestRef,
    })
  },
  methods: {
    ...mapActions(['setCommunicationContext', 'fetchTagCloud']),
    ...mapPiniaActions(useAuditsStore, ['fetchChangesSinceLastVisit']),
    ...mapPiniaActions(useContext, ['setContext', 'setContextName']),
    ...mapPiniaActions(useWorkflowReviewFilter, ['applyFilter']),
    ...mapPiniaActions(useWorfklowReviewItems, [
      'receiveReviewItems',
      'updateReviewItem',
      'updateReviewItemHTML',
      'updateCurrentReviewItemIndex',
    ]),
    ...mapPiniaActions(useWorkflowReviewItemStatus, [
      'receiveItemStatuses',
      'updateReviewItemStatus',
    ]),
    ...mapPiniaActions(useWorfklowUI, [
      'setCompareLedgerId',
      'toggleReloadingLedger',
      'toggleReloadingMode',
      'updateCompareMode',
    ]),
    ...mapPiniaActions(useSidebarFilePreviewStore, [
      'setAttachment',
      'syncFilePreviewDrawerUrlParams',
    ]),
    ...mapPiniaActions(useDrawer, ['openDrawer']),
    async clearFilter() {
      await this.applyFilter('show-all')

      Turbolinks.visit(window.location.href)
    },
    ...mapPiniaActions(useChecksStore, ['resetChecks']),
    async periodPicked(compareLedgerId) {
      this.$refs.periodPickerDropdown.classList.remove('open')
      this.toggleReloadingLedger()
      await this.storePickedPeriod(compareLedgerId)
      this.syncFilePreviewDrawerUrlParams()
      Turbolinks.visit(window.location.href)
    },
    storePickedPeriod(compareLedgerId) {
      this.turnOnCompareMode({
        ledgerId: this.ledgerId,
        workflowId: this.workflowId,
        compareLedgerId,
      })
    },
    subscribeToLiquidUpdates() {
      const requestChannelName = this.messageBusChannelName
        ? `${this.messageBusChannelName}/r/${this.requestRef}`
        : null

      this.polling = new Polling(
        requestChannelName,
        parseInt(this.messageBusLastId ?? '0', 10),
        (response) => {
          this.handleLiquidUpdate(response)
        }
      )

      this.polling.subscribe()
    },
    handleLiquidUpdate(response) {
      if (response.request_ref !== this.requestRef) {
        return
      }

      const items = response.items ?? []
      if (items.length === 0) return

      // Serialize updates so an older message can't overwrite a newer one
      // when decompression resolves out of order.
      this.liquidUpdateChain = this.liquidUpdateChain
        .then(() => Promise.all(items.map(decompressLiquidItem)))
        .then((decompressed) => {
          decompressed.forEach(this.updateReviewItemHTML)
        })
        .catch((error) => {
          SfLog.error('Workflow review liquid payload decompression failed', {
            extra: {
              ledgerId: this.ledgerId,
              workflowId: this.workflowId,
              requestRef: this.requestRef,
              error,
            },
          })
        })
    },
    initLazyBatchLoading() {
      const root = this.$refs.scrollableContent?.$el
      if (!root || this.batches.length === 0) return

      this.scrollAnchor = new FocusedScrollAnchor(
        root,
        this.$refs.reviewItemsContent ?? null
      )

      // Capture clicks on the in-template triggers that open a right drawer (a
      // PDF preview link, a file-collection input). The drawer narrows the list
      // column, reflowing the fluid liquid content, so before that happens we
      // pin the *clicked* item's own template — resolved from its [data-index],
      // never a possibly-stale focused index — and hold it across the reflow and
      // any concurrent batch loads. Capture phase runs before the document-level
      // handlers that open the drawer, so the pin snapshots pre-reflow geometry.
      root.addEventListener('click', this.holdTemplateOpeningDrawer, true)

      // Pin the item the URL/server marked as focused so it holds its position
      // while the batches above it lazy-load and grow, the same hold a tree
      // navigation gets. Deferred until the list items have rendered: the list
      // only renders once scrollable-content has set its observer, so on mount
      // the [data-index] element does not exist yet. scrollToIndex also re-bases
      // expectedScrollTop, clearing the false "user scroll" the item's own
      // mount-time scroll would otherwise trip.
      this.scrollFocusedIntoView()

      this.lazyBatchLoader = new LazyBatchLoader(async (batchIdx, signal) => {
        try {
          await this.executeBatchFetch(batchIdx, signal)
        } catch (error) {
          SfLog.error('Workflow review batch fetch failed', {
            extra: {
              batchIdx,
              ledgerId: this.ledgerId,
              workflowId: this.workflowId,
              error,
            },
          })
          throw error
        }
      })
      this.lazyBatchLoader.setBatches(this.batches)
      this.lazyBatchLoader.init(root)

      // Test hook: lets system specs drive prefetchBatchForItem/whenIdle
      // directly instead of racing scrollIntoView → IntersectionObserver →
      // debounced flush. Read-only; production code does not depend on it.
      window.__sfLazyBatchLoader = this.lazyBatchLoader
    },
    async executeBatchFetch(batchIdx, signal) {
      const batchIndexes = this.batches[batchIdx]
      const items = batchIndexes.map((i) => [
        this.items[i].id,
        this.items[i].itemClass,
      ])
      const compareLedgerId = this.compareMode ? this.compareLedgerId : null

      let data
      try {
        data = await api.getBatch({
          items,
          workflowId: this.workflowId,
          ledgerId: this.ledgerId,
          compareLedgerId,
          requestRef: this.requestRef,
        })
      } catch (error) {
        SfLog.error('Workflow review batch API call failed', {
          extra: {
            batchIdx,
            ledgerId: this.ledgerId,
            workflowId: this.workflowId,
            error,
          },
        })
        throw error
      }

      if (!data) return
      // Component torn down (or loader re-init'd) while we were waiting —
      // drop the response rather than polluting the Pinia store.
      if (signal?.aborted) return

      for (const item of data) {
        this.updateReviewItemStatus({
          parentId: item?.id,
          ...(item?.workflowReviewStatusData || {}),
        })

        this.updateReviewItem({ item })
      }
    },
    // The scroll-driven IntersectionObserver reports whatever item sits in the
    // top band. While the anchor holds a pinned target the user hasn't scrolled
    // away from (a navigation or the initial URL-focused item), that is drift —
    // a neighbour reaching the band as batches grow above the target, not a user
    // action — so ignore it, otherwise the focus, URL and sidebar selection move
    // to the wrong item. A deliberate navigation calls handleNewItemFocus
    // directly and is never gated by this.
    onObserverItemFocused(newIndex) {
      if (this.scrollAnchor?.isHoldingTarget()) {
        return
      }

      this.handleNewItemFocus(newIndex)
    },
    handleNewItemFocus(newIndex) {
      if (this.currentIndex === newIndex) {
        return
      }

      this.updateCurrentReviewItemIndex(newIndex)

      // If this focus follows a real user scroll, pin the item they landed on so
      // it stays put while batches above it lazy-load and grow. No-op for focus
      // drift caused by our own re-pins, so a navigated item is never displaced.
      this.scrollAnchor?.refocus(newIndex)

      this.updateUrl()
      this.debounceSetCommunicationContext(newIndex)
    },
    holdListAcrossDrawerReflow() {
      if (!isUsingTwoDrawerLayout()) return
      if (!this.scrollAnchor || this.scrollAnchor.isHoldingTarget()) return

      const index = this.scrollAnchor.itemIndexInFocusBand()
      if (index === null) return

      this.scrollAnchor.holdFocusedItem(index)
    },
    holdTemplateOpeningDrawer(event) {
      if (!isUsingTwoDrawerLayout()) return

      const trigger = event.target.closest(
        '[data-behaviour~="document-preview-link"], [data-behaviour~="live-inputs-file-collection-input"]'
      )
      if (!trigger) return

      const host = trigger.closest('[data-index]')
      if (!host) return

      const index = Number(host.dataset.index)
      if (Number.isNaN(index)) return

      this.scrollAnchor?.holdFocusedItem(index)
      this.handleNewItemFocus(index)
    },
    async scrollFocusedIntoView() {
      const index = this.currentIndex
      await nextTick()
      this.scrollAnchor?.scrollToIndex(index)
    },
    onContainerScroll() {
      this.scrollAnchor?.handleScroll()
    },
    updateUrl() {
      const item = this.currentReviewItem

      setQueryParam(item.itemClass, item.id, '')
    },
    debouncedSetCommunicationContext(newIndex) {
      const { contextType, contextId, contextHrefParam } = this.items[newIndex]

      if (this.isRightDrawerOpen) {
        this.setCommunicationContext({
          contextType,
          contextId,
          contextHrefParam,
        })
      } else {
        // Keep useContext in sync even when the sidebar is closed — it's the
        // owner source for input-field remark creation (see LiveInputs#handleCommentClick).
        this.setContext(contextType, contextId)
        if (!window.Silverfin?.isCurrentUserPulse?.()) {
          this.fetchChangesSinceLastVisit({ contextId, contextType })
        }
      }
      this.fetchAssistantChecks(newIndex)
    },
    fetchAssistantChecks(index) {
      if (!this.useAssistantChecks) {
        return
      }

      if (this.items[index]) {
        const { contextType, contextId } = this.items[index]
        this.resetChecks(contextType, contextId)
      }
    },
    onRefreshCommunicationData() {
      this.fetchTagCloud({
        workflowId: this.workflowId,
        ledgerId: this.ledgerId,
      })
    },
  },
}

export default WorkflowReview
</script>

<style lang="scss" scoped>
.workflow-review {
  &--narrow {
    max-width: calc(100vw - var(--sidebar-right-width));
  }

  &_empty-state-container {
    max-width: 1020px;
    margin: 71px auto 0;
  }
}
</style>
